import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { DocumentArrowUpIcon, PlusCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import StatementUpload from './StatementUpload';
import PlaidLink from '../../../components/shared/PlaidLink';
import walletService from '../services/walletService';
import { toast } from 'react-hot-toast';

const AccountAddModal = ({ isOpen, onClose, onUploadStatement, onManualAdd }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showPlaidLink, setShowPlaidLink] = useState(false);
  const [processing, setProcessing] = useState(false);

  const handleStatementUpload = async (file) => {
    try {
      setProcessing(true);
      console.log('📄 Processing statement:', file.name);

      // Create FormData for the file
      const formData = new FormData();
      formData.append('statement', file);

      // First, process the statement
      console.log('🔍 Processing statement data...');
      const processResult = await walletService.processStatement(formData);
      
      if (!processResult.success) {
        throw new Error(processResult.message || 'Failed to process statement');
      }

      console.log('✅ Statement processed:', processResult.data);

      // Then create account with the processed data
      console.log('📝 Creating account with transactions...');
      const createResult = await walletService.createAccountWithTransactions(processResult.data);

      console.log('✅ Account created:', createResult);

      // Notify parent component
      onUploadStatement(createResult.data);
      
      // Show success message
      toast.success('Account created successfully!');
      
      // Close modal
      onClose();
    } catch (error) {
      console.error('❌ Error processing statement:', error);
      toast.error(error.message || 'Failed to process statement');
    } finally {
      setProcessing(false);
    }
  };

  const handlePlaidSuccess = (plaidData) => {
    console.log('✅ Plaid connection successful:', plaidData);
    toast.success(`Connected to ${plaidData.institutionName} successfully!`);
    onClose();
    // Refresh the wallet data
    if (onUploadStatement) {
      onUploadStatement();
    }
  };

  const options = [
    {
      id: 'sync',
      title: 'Sync Account',
      description: 'Connect your bank account directly with Plaid',
      icon: ArrowPathIcon,
      disabled: false
    },
    {
      id: 'upload',
      title: 'Upload Statement',
      description: 'Upload a bank statement PDF to auto-fill details (Coming Soon)',
      icon: DocumentArrowUpIcon,
      disabled: true // Temporarily disabled - feature is implemented but needs testing
    },
    {
      id: 'manual',
      title: 'Manual Input',
      description: 'Enter account details manually (Coming Soon)',
      icon: PlusCircleIcon,
      disabled: true // Temporarily disabled - to be implemented
    }
  ];

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-white/10 backdrop-blur-md" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-white/20 w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div className="p-4 sm:p-6">
            {showUpload ? (
              <div>
                <StatementUpload
                  onUpload={handleStatementUpload}
                  onCancel={() => {
                    setShowUpload(false);
                    onClose();
                  }}
                  isProcessing={processing}
                />
              </div>
            ) : showPlaidLink ? (
              <div>
                <Dialog.Title className="text-lg font-semibold text-gray-900 mb-4">
                  Connect Bank Account
                </Dialog.Title>
                <PlaidLink 
                  onSuccess={handlePlaidSuccess}
                  onExit={() => {
                    setShowPlaidLink(false);
                  }}
                />
                <button
                  className="mt-4 w-full py-3 px-4 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 focus:outline-none min-h-[44px]"
                  onClick={() => setShowPlaidLink(false)}
                >
                  Back
                </button>
              </div>
            ) : (
              <div>
                <Dialog.Title className="text-lg font-semibold text-gray-900">
                  Add New Account
                </Dialog.Title>
                <Dialog.Description className="mt-2 text-sm text-gray-500">
                  Choose how you'd like to add your account
                </Dialog.Description>

                <div className="mt-6 space-y-3 sm:space-y-4">
                  {options.map((option) => (
                    <button
                      key={option.id}
                      className={`w-full p-3 sm:p-4 text-left rounded-lg border transition-all min-h-[44px] ${
                        option.disabled
                          ? 'opacity-50 cursor-not-allowed border-white/20 bg-white/50'
                          : 'hover:border-indigo-500/50 hover:bg-white/60 border-white/20 bg-white/50'
                      }`}
                      onClick={() => {
                        if (!option.disabled) {
                          if (option.id === 'upload') {
                            setShowUpload(true);
                          } else if (option.id === 'manual') {
                            onManualAdd();
                            onClose();
                          } else if (option.id === 'sync') {
                            setShowPlaidLink(true);
                          }
                        }
                      }}
                      disabled={option.disabled}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <option.icon 
                            className={`h-5 w-5 sm:h-6 sm:w-6 ${option.disabled ? 'text-gray-400' : 'text-indigo-600'}`}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`font-medium text-sm sm:text-base ${option.disabled ? 'text-gray-400' : 'text-gray-900'}`}>
                            {option.title}
                          </div>
                          <div className={`text-xs sm:text-sm ${option.disabled ? 'text-gray-400' : 'text-gray-600'}`}>
                            {option.description}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                <button
                  className="mt-6 w-full py-3 px-4 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 focus:outline-none min-h-[44px]"
                  onClick={onClose}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default AccountAddModal; 